import { parseUrl } from './parseUrl';

/**
 * Check if a link is an external link by comparing the actual destination hosts.
 *
 * This function properly handles URLs with URL-encoded parameters that may contain
 * the current domain (e.g., Adjust tracking links with fallback URLs).
 *
 * Hint: Please use this function instead of `isInternal` when you want to determine if a link points outside the
 * current domain, as it provides a more accurate check by parsing the URLs and comparing their hosts directly.
 *
 * @param link - The link to check
 * @param url - The current URL to compare against
 * @returns True if the link is external (different host), false if internal or relative
 *
 * @example
 * ```typescript
 * // Internal links
 * isLinkOut('/about', 'https://pixum.de') // false (relative URL)
 * isLinkOut('#section', 'https://pixum.de') // false (hash-only)
 * isLinkOut('https://pixum.de/page', 'https://pixum.de') // false (same host)
 *
 * // External links
 * isLinkOut('https://google.com', 'https://pixum.de') // true (different host)
 * isLinkOut('https://pixum.go.link/open?fallback=https%3A%2F%2Fwww.pixum.de', 'https://pixum.de')
 * // true (external, even though URL-encoded params contain pixum.de)
 *
 * // In conjunction with getBaseUrl()
 *
 * const baseUrl = getBaseUrl(); // e.g., 'https://pixum.de'
 * isLinkOut('https://pixum.de/page', baseUrl) // false (same host)
 * isLinkOut('https://google.com', baseUrl) // true (different host)
 * ```
 */
export function isLinkOut(link: string, url?: string) {
  if (!link || !url) return false;

  // Skip relative URLs and hash-only links (these are always internal)
  if (link.startsWith('/') || link.startsWith('#')) return false;

  try {
    // Parse both URLs to extract actual hosts
    const currentHost = parseUrl(url).host || '';
    const linkHost = parseUrl(link).host || '';

    // Compare the actual destination hosts
    // Empty linkHost means it's not a valid absolute URL (likely relative)
    return linkHost !== '' && linkHost !== currentHost;
  } catch {
    // If parsing fails, assume it's a relative URL (not a link out)
    return false;
  }
}
